import React, { useState, useEffect, useCallback } from "react";
import {
  HiPlus,
  HiClipboard,
  HiCalendarDays,
  HiCheckCircle,
  HiUsers,
  HiArrowPath,
  HiCursorArrowRays,
  HiChartBar,
  HiXMark,
  HiUserPlus,
} from "react-icons/hi2";
import { firebaseCacheService } from "../../services/firebaseCacheService";
import {
  collection,
  query,
  orderBy,
  getDocs,
  getDoc,
  doc,
  where,
  limit,
  updateDoc,
  arrayUnion,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { userService } from "../../services/userService";
import type { CampaignSignupRow } from "../../services/userService";

interface Campaign {
  id: string;
  campaignCode: string;
  createdAt: number;
  createdBy?: string;
  userCount?: number;
  clicks?: number;
  lastClicked?: number;
  /** Per-click timestamps (Firestore Timestamp); populated by trackCampaignClick */
  clickTimesList?: Timestamp[];
  /** When true, signups are counted via onUserDocCreated on campaign docs; otherwise from user query */
  signUpsTracked?: boolean;
  signUps?: number;
  signUpsList?: unknown[];
  purchasesCount?: number;
  purchaseHistoryList?: unknown[];
  affiliateInfo?: {
    userID?: string;
    affiliateDurationsTimesList?: unknown[];
  };
  affiliateDisplayName?: string;
}

type CampaignPurchaseRow = {
  userId: string;
  product: string;
  priceMinor: number;
  currency: string;
  timestamp: Date | null;
};

type AffiliateDurationRow = {
  StartOrEnd: "start" | "end";
  timestamp: Date | null;
};

type AffiliateCandidate = {
  userId: string;
  email: string;
  username: string;
  displayName: string;
};

function clickEntryToDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (
    typeof value === "object" &&
    "seconds" in (value as Record<string, unknown>)
  ) {
    const s = (value as { seconds: number }).seconds;
    const ns = (value as { nanoseconds?: number }).nanoseconds ?? 0;
    return new Timestamp(s, ns).toDate();
  }
  return null;
}

function calendarDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildDailyClickFrequency(
  clickTimesList: unknown[] | undefined
): { dayKey: string; count: number; label: string }[] {
  if (!clickTimesList?.length) return [];

  const counts = new Map<string, number>();
  for (const entry of clickTimesList) {
    const d = clickEntryToDate(entry);
    if (!d) continue;
    const key = calendarDayKey(d);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const sortedKeys = Array.from(counts.keys()).sort();
  return sortedKeys.map((dayKey) => {
    const [y, mo, da] = dayKey.split("-").map(Number);
    const label = new Date(y, mo - 1, da).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return { dayKey, count: counts.get(dayKey) ?? 0, label };
  });
}

function buildPurchaseRows(
  purchaseHistoryList: unknown[] | undefined
): CampaignPurchaseRow[] {
  if (!purchaseHistoryList?.length) return [];

  const rows: CampaignPurchaseRow[] = [];
  for (const entry of purchaseHistoryList) {
    if (!entry || typeof entry !== "object") continue;
    const obj = entry as Record<string, unknown>;
    const userId =
      typeof obj.userId === "string" && obj.userId.trim()
        ? obj.userId.trim()
        : "unknown";
    const product =
      typeof obj.product === "string" && obj.product.trim()
        ? obj.product.trim()
        : "Unknown product";
    const priceMinor = typeof obj.price === "number" ? obj.price : 0;
    const currencyRaw =
      typeof obj.currency === "string" ? obj.currency.trim().toUpperCase() : "";
    const currency = /^[A-Z]{3}$/.test(currencyRaw) ? currencyRaw : "USD";
    const timestamp = clickEntryToDate(obj.timestamp);
    rows.push({ userId, product, priceMinor, currency, timestamp });
  }

  rows.sort((a, b) => {
    const ta = a.timestamp?.getTime() ?? 0;
    const tb = b.timestamp?.getTime() ?? 0;
    return tb - ta;
  });
  return rows;
}

const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF",
  "CLP",
  "DJF",
  "GNF",
  "JPY",
  "KMF",
  "KRW",
  "MGA",
  "PYG",
  "RWF",
  "UGX",
  "VND",
  "VUV",
  "XAF",
  "XOF",
  "XPF",
]);

function buildAffiliateDurationRows(
  affiliateDurationsTimesList: unknown[] | undefined
): AffiliateDurationRow[] {
  if (!affiliateDurationsTimesList?.length) return [];

  const rows: AffiliateDurationRow[] = [];
  for (const entry of affiliateDurationsTimesList) {
    if (!entry || typeof entry !== "object") continue;
    const obj = entry as Record<string, unknown>;
    const rawKind = obj.StartOrEnd;
    const StartOrEnd = rawKind === "end" ? "end" : "start";
    const timestamp = clickEntryToDate(obj.Timestamp);
    rows.push({ StartOrEnd, timestamp });
  }

  rows.sort((a, b) => {
    const ta = a.timestamp?.getTime() ?? 0;
    const tb = b.timestamp?.getTime() ?? 0;
    return tb - ta;
  });
  return rows;
}

function isWithinAffiliateDuration(
  eventTime: Date | null,
  affiliateDurationsTimesList: unknown[] | undefined
): boolean {
  if (!eventTime || !affiliateDurationsTimesList?.length) return false;

  const chronologicalEvents = buildAffiliateDurationRows(
    affiliateDurationsTimesList
  )
    .filter((row) => row.timestamp !== null)
    .sort(
      (a, b) =>
        (a.timestamp?.getTime() ?? Number.POSITIVE_INFINITY) -
        (b.timestamp?.getTime() ?? Number.POSITIVE_INFINITY)
    );

  let activeStartMs: number | null = null;
  const windows: Array<{ startMs: number; endMs: number | null }> = [];

  for (const event of chronologicalEvents) {
    const ts = event.timestamp?.getTime();
    if (typeof ts !== "number") continue;

    if (event.StartOrEnd === "start") {
      activeStartMs = ts;
      continue;
    }

    if (activeStartMs !== null) {
      windows.push({ startMs: activeStartMs, endMs: ts });
      activeStartMs = null;
    }
  }

  if (activeStartMs !== null) {
    windows.push({ startMs: activeStartMs, endMs: null });
  }

  const t = eventTime.getTime();
  return windows.some(
    (w) => t >= w.startMs && (w.endMs === null || t <= w.endMs)
  );
}

const CampaignManagement: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCampaignCode, setNewCampaignCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [refreshingCounts, setRefreshingCounts] = useState(false);
  const [clickFrequencyCampaign, setClickFrequencyCampaign] =
    useState<Campaign | null>(null);
  const [signupDialogCampaign, setSignupDialogCampaign] =
    useState<Campaign | null>(null);
  const [signupRows, setSignupRows] = useState<CampaignSignupRow[] | null>(
    null
  );
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [purchaseDialogCampaign, setPurchaseDialogCampaign] =
    useState<Campaign | null>(null);
  const [purchaseRows, setPurchaseRows] = useState<CampaignPurchaseRow[] | null>(
    null
  );
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  // Map of currency code -> AUD value of 1 unit of that currency (e.g. USD -> 1.52)
  const [fxRatesToAud, setFxRatesToAud] = useState<Record<string, number> | null>(
    null
  );
  const [fxRatesLoading, setFxRatesLoading] = useState(false);
  const [fxRatesError, setFxRatesError] = useState<string | null>(null);
  const [affiliateDialogCampaign, setAffiliateDialogCampaign] =
    useState<Campaign | null>(null);
  const [affiliateEmail, setAffiliateEmail] = useState("");
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [affiliateError, setAffiliateError] = useState<string | null>(null);
  const [affiliateCandidate, setAffiliateCandidate] =
    useState<AffiliateCandidate | null>(null);

  const getAffiliateDisplayName = async (
    affiliateUserId: string | undefined
  ): Promise<string | undefined> => {
    const userId = affiliateUserId?.trim();
    if (!userId) return undefined;
    try {
      const indexedSnap = await getDoc(doc(db, "indexedUserData", userId));
      if (!indexedSnap.exists()) return userId;
      const data = indexedSnap.data() as {
        name?: string;
        surname?: string;
        username?: string;
      };
      const fullName = `${data.name ?? ""} ${data.surname ?? ""}`.trim();
      return fullName || data.username?.trim() || userId;
    } catch (error) {
      console.warn("Unable to load affiliate display name:", error);
      return userId;
    }
  };

  const withAffiliateDisplayName = async (
    campaign: Campaign
  ): Promise<Campaign> => {
    const affiliateDisplayName = await getAffiliateDisplayName(
      campaign.affiliateInfo?.userID
    );
    return { ...campaign, affiliateDisplayName };
  };

  // Production copies links to the signup site. Other hosts keep their own origin.
  const getCampaignUrl = (campaignCode: string): string => {
    if (window.location.hostname === "peak.app") {
      return `https://signup.peak.app/?code=${encodeURIComponent(campaignCode)}`;
    }
    return `${window.location.origin}/?code=${encodeURIComponent(campaignCode)}`;
  };

  // Get user count for a specific campaign code
  const getUserCountForCampaign = async (
    campaignCode: string
  ): Promise<number> => {
    try {
      return await userService.getUserCountForCampaign(campaignCode);
    } catch (error) {
      console.error(
        `Error counting users for campaign ${campaignCode}:`,
        error
      );
      return 0;
    }
  };

  // Refresh user counts for all campaigns
  const refreshUserCounts = async () => {
    try {
      setRefreshingCounts(true);
      const updatedCampaigns = await Promise.all(
        campaigns.map(async (campaign) => {
          if (campaign.signUpsTracked === true) {
            const snap = await getDoc(doc(db, "campaigns", campaign.id));
            const d = snap.data();
            const signUps =
              typeof d?.signUps === "number"
                ? d.signUps
                : (campaign.signUps ?? 0);
            return {
              ...campaign,
              ...(d ?? {}),
              id: campaign.id,
              userCount: signUps,
            } as Campaign;
          }
          const userCount = await getUserCountForCampaign(
            campaign.campaignCode
          );
          return {
            ...campaign,
            userCount,
          };
        })
      );
      const enrichedCampaigns = await Promise.all(
        updatedCampaigns.map(withAffiliateDisplayName)
      );
      setCampaigns(enrichedCampaigns);
    } catch (error) {
      console.error("Error refreshing user counts:", error);
    } finally {
      setRefreshingCounts(false);
    }
  };

  // Load campaigns from Firebase
  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const campaignsRef = collection(db, "campaigns");
      const q = query(campaignsRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);

      const campaignsList: Campaign[] = [];
      querySnapshot.forEach((doc) => {
        campaignsList.push({
          id: doc.id,
          ...doc.data(),
        } as Campaign);
      });

      // User counts: tracked campaigns use signUps on the doc; legacy uses users query
      const campaignsWithCounts = await Promise.all(
        campaignsList.map(async (campaign) => {
          if (campaign.signUpsTracked === true) {
            return {
              ...campaign,
              userCount:
                typeof campaign.signUps === "number" ? campaign.signUps : 0,
            };
          }
          const userCount = await getUserCountForCampaign(
            campaign.campaignCode
          );
          return {
            ...campaign,
            userCount,
          };
        })
      );

      const enrichedCampaigns = await Promise.all(
        campaignsWithCounts.map(withAffiliateDisplayName)
      );
      setCampaigns(enrichedCampaigns);
    } catch (error) {
      console.error("Error loading campaigns:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate campaign code
  const validateCampaignCode = (code: string): string | null => {
    if (!code.trim()) {
      return "Campaign code is required";
    }
    if (code.length < 3) {
      return "Campaign code must be at least 3 characters";
    }
    if (code.length > 20) {
      return "Campaign code must be less than 20 characters";
    }
    if (!/^[A-Z0-9_-]+$/i.test(code)) {
      return "Campaign code can only contain letters, numbers, underscores, and hyphens";
    }
    if (
      campaigns.some(
        (campaign) => campaign.campaignCode.toLowerCase() === code.toLowerCase()
      )
    ) {
      return "This campaign code already exists";
    }
    return null;
  };

  // Create new campaign
  const createCampaign = async () => {
    try {
      setCreating(true);
      setCodeError(null);

      const campaignCode = newCampaignCode.trim();
      const validationError = validateCampaignCode(campaignCode);

      if (validationError) {
        setCodeError(validationError);
        return;
      }

      const timestamp = Date.now();

      const campaignData: Omit<Campaign, "id"> = {
        campaignCode,
        createdAt: timestamp,
        createdBy: "admin", // You can integrate with auth context to get actual user
        signUpsTracked: true,
        signUps: 0,
        signUpsList: [],
      };

      // Save to Firebase using a unique document ID
      const docId = `campaign_${timestamp}_${campaignCode}`;
      await firebaseCacheService.setDocument("campaigns", docId, campaignData);

      // Add to local state with initial user count of 0
      setCampaigns((prev) => [
        {
          id: docId,
          ...campaignData,
          userCount: 0,
        },
        ...prev,
      ]);

      // Reset form
      setNewCampaignCode("");
      setShowCreateForm(false);
    } catch (error) {
      console.error("Error creating campaign:", error);
      setCodeError("Failed to create campaign. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  // Copy campaign link to clipboard
  const copyCampaignLink = async (campaignCode: string) => {
    const campaignUrl = getCampaignUrl(campaignCode);

    try {
      await navigator.clipboard.writeText(campaignUrl);
      setCopiedCode(campaignCode);

      // Clear copied state after 2 seconds
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error("Failed to copy campaign link:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = campaignUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedCode(campaignCode);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  // Format date for display
  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSignupDate = (d: Date | null): string => {
    if (!d) return "Not available";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPurchaseAmount = (priceMinor: number, currency: string): string => {
    if (!Number.isFinite(priceMinor)) return "N/A";
    const normalizedCurrency = /^[A-Z]{3}$/.test(currency) ? currency : "USD";
    const divisor = ZERO_DECIMAL_CURRENCIES.has(normalizedCurrency) ? 1 : 100;
    const amountMajor = priceMinor / divisor;

    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: normalizedCurrency,
      }).format(amountMajor);
    } catch {
      return `${amountMajor.toFixed(2)} ${normalizedCurrency}`;
    }
  };

  const formatPriceInAud = (
    priceMinor: number,
    currency: string,
    rates: Record<string, number> | null
  ): string => {
    if (!Number.isFinite(priceMinor)) return "N/A";
    const normalizedCurrency = /^[A-Z]{3}$/.test(currency) ? currency : "USD";
    const divisor = ZERO_DECIMAL_CURRENCIES.has(normalizedCurrency) ? 1 : 100;
    const amountMajor = priceMinor / divisor;

    if (normalizedCurrency === "AUD") {
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
      }).format(amountMajor);
    }

    const rate = rates?.[normalizedCurrency];
    if (!rate || !Number.isFinite(rate) || rate <= 0) return "—";

    const audAmount = amountMajor * rate;
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(audAmount);
  };

  // Fetch FX rates from @fawazahmed0/currency-api (free, no key, CORS-friendly).
  // Single AUD -> X call, then inverted so result maps currency -> AUD per 1 unit.
  const fetchAudRates = async (
    currencies: string[]
  ): Promise<Record<string, number>> => {
    const unique = Array.from(
      new Set(
        currencies
          .map((c) => c.toUpperCase())
          .filter((c) => /^[A-Z]{3}$/.test(c) && c !== "AUD")
      )
    );
    if (unique.length === 0) return {};

    const endpoints = [
      "https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/aud.json",
      "https://latest.currency-api.pages.dev/v1/currencies/aud.json",
    ];

    let lastError: unknown = null;
    for (const url of endpoints) {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          lastError = new Error(`Currency API ${url} -> ${res.status}`);
          continue;
        }
        const json = (await res.json()) as {
          aud?: Record<string, number>;
        };
        const audToX = json.aud ?? {};
        const inverted: Record<string, number> = {};
        for (const code of unique) {
          const audPerOne = audToX[code.toLowerCase()];
          if (typeof audPerOne === "number" && audPerOne > 0) {
            inverted[code] = 1 / audPerOne;
          }
        }
        return inverted;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error("Failed to fetch FX rates");
  };

  const refreshCampaignFromDb = async (campaignId: string) => {
    const snap = await getDoc(doc(db, "campaigns", campaignId));
    if (!snap.exists()) return;
    const latest = await withAffiliateDisplayName(snap.data() as Campaign);
    setCampaigns((prev) =>
      prev.map((c) => (c.id === campaignId ? { ...c, ...latest, id: c.id } : c))
    );
    setAffiliateDialogCampaign((prev) =>
      prev?.id === campaignId ? { ...prev, ...latest, id: prev.id } : prev
    );
  };

  const lookupAffiliateCandidateByEmail = async (
    email: string
  ): Promise<AffiliateCandidate | null> => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return null;

    const variants = Array.from(
      new Set([trimmedEmail, trimmedEmail.toLowerCase()])
    );

    for (const variant of variants) {
      const q = query(
        collection(db, "indexedUserData"),
        where("email", "==", variant),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) continue;

      const indexedDoc = snap.docs[0];
      const indexedData = indexedDoc.data() as {
        id?: string;
        email?: string;
        name?: string;
        surname?: string;
        username?: string;
      };
      const userId = indexedDoc.id;
      const name = `${indexedData.name ?? ""} ${indexedData.surname ?? ""}`.trim();
      const username = indexedData.username?.trim() || userId;

      return {
        userId,
        email: (indexedData.email ?? variant).toString(),
        username,
        displayName: name || username,
      };
    }

    return null;
  };

  const openAffiliateDialog = (campaign: Campaign) => {
    setAffiliateDialogCampaign(campaign);
    setAffiliateEmail("");
    setAffiliateError(null);
    setAffiliateCandidate(null);
  };

  const closeAffiliateDialog = () => {
    setAffiliateDialogCampaign(null);
    setAffiliateEmail("");
    setAffiliateError(null);
    setAffiliateCandidate(null);
  };

  const searchAffiliateUser = async () => {
    setAffiliateError(null);
    setAffiliateCandidate(null);
    setAffiliateLoading(true);
    try {
      const email = affiliateEmail.trim();
      if (!email) {
        setAffiliateError("Affiliate email is required");
        return;
      }

      const candidate = await lookupAffiliateCandidateByEmail(email);
      if (!candidate) {
        setAffiliateError("No user found with this email");
        return;
      }

      setAffiliateCandidate(candidate);
    } catch (err) {
      console.error("Error searching affiliate user:", err);
      setAffiliateError(
        err instanceof Error ? err.message : "Failed to search for user"
      );
    } finally {
      setAffiliateLoading(false);
    }
  };

  const addAffiliateToCampaign = async () => {
    if (!affiliateDialogCampaign || !affiliateCandidate) return;
    setAffiliateError(null);
    setAffiliateLoading(true);
    try {
      const newEntry = {
        StartOrEnd: "start" as const,
        Timestamp: Timestamp.now(),
      };

      await updateDoc(doc(db, "campaigns", affiliateDialogCampaign.id), {
        affiliateInfo: {
          userID: affiliateCandidate.userId,
          affiliateDurationsTimesList: [newEntry],
        },
      });

      await refreshCampaignFromDb(affiliateDialogCampaign.id);
      setAffiliateEmail("");
      setAffiliateCandidate(null);
    } catch (err) {
      console.error("Error adding affiliate:", err);
      setAffiliateError(
        err instanceof Error ? err.message : "Failed to add affiliate"
      );
    } finally {
      setAffiliateLoading(false);
    }
  };

  const appendAffiliateStatus = async (nextStatus: "start" | "end") => {
    if (!affiliateDialogCampaign?.affiliateInfo?.userID) return;
    setAffiliateError(null);
    setAffiliateLoading(true);
    try {
      const rows = buildAffiliateDurationRows(
        affiliateDialogCampaign.affiliateInfo.affiliateDurationsTimesList
      );
      const latest = rows[0]?.StartOrEnd;
      if (latest === nextStatus) {
        setAffiliateError(
          nextStatus === "start"
            ? "Affiliate program is already running"
            : "Affiliate program is already paused"
        );
        return;
      }

      const entry = {
        StartOrEnd: nextStatus,
        Timestamp: Timestamp.now(),
      };
      await updateDoc(doc(db, "campaigns", affiliateDialogCampaign.id), {
        "affiliateInfo.affiliateDurationsTimesList": arrayUnion(entry),
      });
      await refreshCampaignFromDb(affiliateDialogCampaign.id);
    } catch (err) {
      console.error("Error updating affiliate status:", err);
      setAffiliateError(
        err instanceof Error ? err.message : "Failed to update affiliate status"
      );
    } finally {
      setAffiliateLoading(false);
    }
  };

  const openPurchasesDialog = async (campaign: Campaign) => {
    setPurchaseDialogCampaign(campaign);
    setPurchaseRows(null);
    setPurchaseError(null);
    setFxRatesToAud(null);
    setFxRatesError(null);
    setPurchaseLoading(true);
    try {
      const snap = await getDoc(doc(db, "campaigns", campaign.id));
      const list = snap.data()?.purchaseHistoryList;
      const rows = buildPurchaseRows(list);
      setPurchaseRows(rows);

      const nonAudCurrencies = rows
        .map((r) => r.currency)
        .filter((c) => c && c !== "AUD");
      if (nonAudCurrencies.length > 0) {
        setFxRatesLoading(true);
        try {
          const rates = await fetchAudRates(nonAudCurrencies);
          setFxRatesToAud(rates);
        } catch (fxErr) {
          console.error("Error fetching FX rates:", fxErr);
          setFxRatesError(
            fxErr instanceof Error
              ? fxErr.message
              : "Failed to load AUD conversion rates"
          );
        } finally {
          setFxRatesLoading(false);
        }
      } else {
        setFxRatesToAud({});
      }
    } catch (err) {
      console.error("Error loading campaign purchases:", err);
      setPurchaseError(
        err instanceof Error ? err.message : "Failed to load purchases"
      );
      setPurchaseRows([]);
    } finally {
      setPurchaseLoading(false);
    }
  };

  const closePurchasesDialog = () => {
    setPurchaseDialogCampaign(null);
    setPurchaseRows(null);
    setPurchaseError(null);
    setFxRatesToAud(null);
    setFxRatesError(null);
    setFxRatesLoading(false);
  };

  const openSignupsDialog = async (campaign: Campaign) => {
    setSignupDialogCampaign(campaign);
    setSignupRows(null);
    setSignupError(null);
    setSignupLoading(true);
    try {
      if (campaign.signUpsTracked === true) {
        const snap = await getDoc(doc(db, "campaigns", campaign.id));
        const list = snap.data()?.signUpsList;
        const rows = await userService.hydrateCampaignSignUpsList(list);
        setSignupRows(rows);
      } else {
        const rows = await userService.getUsersForCampaign(
          campaign.campaignCode
        );
        setSignupRows(rows);
      }
    } catch (err) {
      console.error("Error loading campaign signups:", err);
      setSignupError(
        err instanceof Error ? err.message : "Failed to load signups"
      );
      setSignupRows([]);
    } finally {
      setSignupLoading(false);
    }
  };

  const closeSignupsDialog = () => {
    setSignupDialogCampaign(null);
    setSignupRows(null);
    setSignupError(null);
  };

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const clickFrequencyRows = clickFrequencyCampaign
    ? buildDailyClickFrequency(clickFrequencyCampaign.clickTimesList)
    : [];
  const maxDailyClicks = clickFrequencyRows.reduce(
    (m, r) => Math.max(m, r.count),
    0
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-semibold mb-4">Campaign Management</h1>

        {showCreateForm ? (
          <div className="bg-base-200 p-6 rounded-lg border border-base-300">
            <h2 className="text-lg font-medium mb-4">Create New Campaign</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-base-content/80 mb-2">
                  Campaign Code
                </label>
                <input
                  type="text"
                  value={newCampaignCode}
                  onChange={(e) => {
                    setNewCampaignCode(e.target.value);
                    setCodeError(null);
                  }}
                  placeholder="e.g., SUMMER2024 or LAUNCH_WEEK"
                  className={`w-full px-3 py-2 border rounded-lg bg-base-100 focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    codeError ? "border-red-500" : "border-base-300"
                  }`}
                  disabled={creating}
                />
                {codeError && (
                  <p className="text-sm text-red-600 mt-1">{codeError}</p>
                )}
                <p className="text-sm text-base-content/60 mt-1">
                  Use letters, numbers, underscores, and hyphens only. 3-20
                  characters.
                </p>
              </div>

              <div className="flex justify-between gap-3">
                <button
                  onClick={createCampaign}
                  disabled={creating || !newCampaignCode.trim()}
                  className="bg-neutral py-3 px-6 rounded-md text-white hover:bg-neutral/90 flex items-center gap-2"
                >
                  <HiPlus className="w-5 h-5" />
                  {creating ? "Creating..." : "Create Campaign"}
                </button>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewCampaignCode("");
                    setCodeError(null);
                  }}
                  disabled={creating}
                  className=" bg-base-300 py-3 px-6 rounded-md text-base-content/80 hover:bg-base-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <button
              onClick={() => setShowCreateForm(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <HiPlus className="w-5 h-5" />
              Create Campaign
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-200">
          {campaigns.length === 0 ? (
            <div className="p-12 text-center">
              <HiCalendarDays className="w-16 h-16 text-base-content/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-base-content mb-2">
                No campaigns yet
              </h3>
              <p className="text-base-content/70 mb-4">
                Create your first campaign to get started.
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="btn btn-primary"
              >
                <HiPlus className="w-5 h-5 mr-2" />
                Create Campaign
              </button>
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-base-300 flex items-center justify-between">
                <h2 className="text-lg font-medium text-base-content">
                  All Campaigns ({campaigns.length})
                </h2>
                <button
                  onClick={refreshUserCounts}
                  disabled={refreshingCounts}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    refreshingCounts
                      ? "bg-base-200 text-base-content/50 cursor-not-allowed"
                      : "bg-base-200 text-base-content/80 hover:bg-base-300"
                  }`}
                  title="Refresh user counts"
                >
                  <HiArrowPath
                    className={`w-4 h-4 ${
                      refreshingCounts ? "animate-spin" : ""
                    }`}
                  />
                  {refreshingCounts ? "Refreshing..." : "Refresh Counts"}
                </button>
              </div>
              <div className="divide-y divide-base-300">
                {campaigns.map((campaign) => (
                  <div key={campaign.id} className="px-6 py-4 hover:bg-base-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-mono font-medium">
                            {campaign.campaignCode}
                          </div>
                          <div className="bg-green-500/15 text-green-700 dark:text-green-400 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                            <HiUsers className="w-3 h-3" />
                            {campaign.userCount || 0} users
                          </div>
                          <div className="bg-blue-500/15 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                            <HiCursorArrowRays className="w-3 h-3" />
                            {campaign.clicks || 0} clicks
                          </div>
                          <div className="bg-purple-500/15 text-purple-700 dark:text-purple-400 px-2 py-1 rounded-full text-xs font-medium">
                            {campaign.purchasesCount || 0} purchases
                          </div>
                          <div className="text-sm text-base-content/60 flex items-center gap-1">
                            <HiCalendarDays className="w-4 h-4" />
                            Created {formatDate(campaign.createdAt)}
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-base-content/70">
                          <span className="font-medium">Campaign URL:</span>{" "}
                          <span className="font-mono text-base-content">
                            {getCampaignUrl(campaign.campaignCode)}
                          </span>
                        </div>
                        {campaign.affiliateInfo?.userID && (
                          <div className="mt-1 text-sm">
                            <span className="font-medium">
                              Affiliate campaign:
                            </span>{" "}
                            <span>
                              {campaign.affiliateDisplayName ||
                                campaign.affiliateInfo.userID}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => setClickFrequencyCampaign(campaign)}
                          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors bg-base-200 text-base-content/80 hover:bg-base-300"
                          title="View clicks per calendar day"
                        >
                          <HiChartBar className="w-4 h-4" />
                          Daily clicks
                        </button>
                        <button
                          type="button"
                          onClick={() => openSignupsDialog(campaign)}
                          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors bg-base-200 text-base-content/80 hover:bg-base-300"
                          title="Users who signed up with this campaign code"
                        >
                          <HiUserPlus className="w-4 h-4" />
                          Signups
                        </button>
                        <button
                          type="button"
                          onClick={() => openPurchasesDialog(campaign)}
                          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors bg-base-200 text-base-content/80 hover:bg-base-300"
                          title="Purchases attributed to this campaign"
                        >
                          Purchases
                        </button>
                        <button
                          type="button"
                          onClick={() => openAffiliateDialog(campaign)}
                          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors bg-base-200 text-base-content/80 hover:bg-base-300"
                          title="Manage campaign affiliate"
                        >
                          {campaign.affiliateInfo?.userID
                            ? "Edit Affiliate"
                            : "Add Affiliate"}
                        </button>
                        <button
                          type="button"
                          onClick={() => copyCampaignLink(campaign.campaignCode)}
                          className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            copiedCode === campaign.campaignCode
                              ? "bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30"
                              : "bg-base-200 text-base-content/80 hover:bg-base-300"
                          }`}
                        >
                          {copiedCode === campaign.campaignCode ? (
                            <>
                              <HiCheckCircle className="w-4 h-4" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <HiClipboard className="w-4 h-4" />
                              Copy Link
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {clickFrequencyCampaign && (
        <div className="modal modal-open z-50">
          <div className="modal-box max-w-lg">
            <div className="flex justify-between items-start gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold">Daily click frequency</h3>
                <p className="text-sm text-base-content/70 font-mono mt-1">
                  {clickFrequencyCampaign.campaignCode}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost shrink-0"
                onClick={() => setClickFrequencyCampaign(null)}
                aria-label="Close"
              >
                <HiXMark className="w-5 h-5" />
              </button>
            </div>

            {clickFrequencyRows.length === 0 ? (
              <p className="text-sm text-base-content/70 py-6 text-center">
                No per-click timestamps yet. New clicks are recorded after this
                update is deployed.
              </p>
            ) : (
              <div className="space-y-3 max-h-[min(60vh,24rem)] overflow-y-auto pr-1">
                {clickFrequencyRows.map((row) => (
                  <div key={row.dayKey} className="flex flex-col gap-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-base-content/90">{row.label}</span>
                      <span className="font-medium tabular-nums">
                        {row.count}{" "}
                        {row.count === 1 ? "click" : "clicks"}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-base-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary/80 transition-[width]"
                        style={{
                          width:
                            maxDailyClicks > 0
                              ? `${(row.count / maxDailyClicks) * 100}%`
                              : "0%",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="modal-action mt-6">
              <button
                type="button"
                className="btn"
                onClick={() => setClickFrequencyCampaign(null)}
              >
                Close
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop bg-black/50"
            role="presentation"
            onClick={() => setClickFrequencyCampaign(null)}
          />
        </div>
      )}

      {signupDialogCampaign && (
        <div className="modal modal-open z-50">
          <div className="modal-box max-w-3xl">
            <div className="flex justify-between items-start gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold">Campaign signups</h3>
                <p className="text-sm text-base-content/70 font-mono mt-1">
                  {signupDialogCampaign.campaignCode}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost shrink-0"
                onClick={closeSignupsDialog}
                aria-label="Close"
              >
                <HiXMark className="w-5 h-5" />
              </button>
            </div>

            {signupLoading ? (
              <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-md text-primary" />
              </div>
            ) : signupError ? (
              <p className="text-sm text-error py-6 text-center">{signupError}</p>
            ) : signupRows && signupRows.length === 0 ? (
              <p className="text-sm text-base-content/70 py-6 text-center">
                No users have this campaign code on their profile yet.
              </p>
            ) : (
              <div className="overflow-x-auto max-h-[min(60vh,28rem)] overflow-y-auto">
                <table className="table table-sm table-zebra w-full">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Sign-up date</th>
                      <th>Affiliated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signupRows?.map((row) => (
                      <tr key={row.userId}>
                        <td className="whitespace-nowrap">{row.displayName}</td>
                        <td className="font-mono text-sm">{row.username}</td>
                        <td className="text-sm max-w-[14rem] truncate">
                          {row.email ?? "—"}
                        </td>
                        <td className="text-sm whitespace-nowrap tabular-nums">
                          {formatSignupDate(row.signupAt)}
                        </td>
                        <td>
                          {isWithinAffiliateDuration(
                            row.signupAt,
                            signupDialogCampaign.affiliateInfo
                              ?.affiliateDurationsTimesList
                          ) ? (
                            <span className="badge badge-success badge-sm">Yes</span>
                          ) : (
                            <span className="badge badge-error badge-sm">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-base-content/50 mt-3">
              {signupDialogCampaign.signUpsTracked === true
                ? "Sign-up times are recorded on the campaign when each user profile is created (Cloud Function)."
                : "Sign-up date uses profile account creation when available; older profiles may not have a date."}
            </p>

            <div className="modal-action mt-4">
              <button type="button" className="btn" onClick={closeSignupsDialog}>
                Close
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop bg-black/50"
            role="presentation"
            onClick={closeSignupsDialog}
          />
        </div>
      )}

      {purchaseDialogCampaign && (
        <div className="modal modal-open z-50">
          <div className="modal-box w-11/12 max-w-5xl">
            <div className="flex justify-between items-start gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold">Campaign purchases</h3>
                <p className="text-sm text-base-content/70 font-mono mt-1">
                  {purchaseDialogCampaign.campaignCode}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost shrink-0"
                onClick={closePurchasesDialog}
                aria-label="Close"
              >
                <HiXMark className="w-5 h-5" />
              </button>
            </div>

            {purchaseLoading ? (
              <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-md text-primary" />
              </div>
            ) : purchaseError ? (
              <p className="text-sm text-error py-6 text-center">
                {purchaseError}
              </p>
            ) : purchaseRows && purchaseRows.length === 0 ? (
              <p className="text-sm text-base-content/70 py-6 text-center">
                No purchases have been tracked for this campaign yet.
              </p>
            ) : (
              <div className="overflow-x-auto max-h-[min(60vh,28rem)] overflow-y-auto">
                {fxRatesError && (
                  <p className="text-xs text-warning mb-2">
                    AUD conversion unavailable: {fxRatesError}
                  </p>
                )}
                <table className="table table-sm table-zebra w-full">
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Product</th>
                      <th>Price</th>
                      <th>
                        Price AUD
                        {fxRatesLoading && (
                          <span className="loading loading-spinner loading-xs ml-2 align-middle" />
                        )}
                      </th>
                      <th>Timestamp</th>
                      <th>Affiliated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseRows?.map((row, idx) => (
                      <tr key={`${row.userId}-${row.timestamp?.getTime() ?? idx}`}>
                        <td className="font-mono text-sm">{row.userId}</td>
                        <td className="text-sm">{row.product}</td>
                        <td className="text-sm whitespace-nowrap tabular-nums">
                          {formatPurchaseAmount(row.priceMinor, row.currency)}
                        </td>
                        <td className="text-sm whitespace-nowrap tabular-nums">
                          {fxRatesLoading
                            ? "…"
                            : formatPriceInAud(
                                row.priceMinor,
                                row.currency,
                                fxRatesToAud
                              )}
                        </td>
                        <td className="text-sm whitespace-nowrap tabular-nums">
                          {formatSignupDate(row.timestamp)}
                        </td>
                        <td>
                          {isWithinAffiliateDuration(
                            row.timestamp,
                            purchaseDialogCampaign.affiliateInfo
                              ?.affiliateDurationsTimesList
                          ) ? (
                            <span className="badge badge-success badge-sm">Yes</span>
                          ) : (
                            <span className="badge badge-error badge-sm">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-base-content/50 mt-3">
              Purchase price is formatted using the stored currency for each
              transaction.
            </p>

            <div className="modal-action mt-4">
              <button type="button" className="btn" onClick={closePurchasesDialog}>
                Close
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop bg-black/50"
            role="presentation"
            onClick={closePurchasesDialog}
          />
        </div>
      )}

      {affiliateDialogCampaign && (
        <div className="modal modal-open z-50">
          <div className="modal-box max-w-2xl">
            <div className="flex justify-between items-start gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold">
                  {affiliateDialogCampaign.affiliateInfo?.userID
                    ? "Edit Affiliate"
                    : "Add Affiliate"}
                </h3>
                <p className="text-sm text-base-content/70 font-mono mt-1">
                  {affiliateDialogCampaign.campaignCode}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost shrink-0"
                onClick={closeAffiliateDialog}
                aria-label="Close"
              >
                <HiXMark className="w-5 h-5" />
              </button>
            </div>

            {!affiliateDialogCampaign.affiliateInfo?.userID ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-base-content/80 mb-2">
                    Affiliate email
                  </label>
                  <input
                    type="email"
                    value={affiliateEmail}
                    onChange={(e) => {
                      setAffiliateEmail(e.target.value);
                      setAffiliateCandidate(null);
                      setAffiliateError(null);
                    }}
                    placeholder="affiliate@example.com"
                    className="w-full px-3 py-2 border border-base-300 rounded-lg bg-base-100 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    disabled={affiliateLoading}
                  />
                </div>
                {affiliateCandidate && (
                  <div className="bg-base-200 rounded-lg p-4 text-sm space-y-1">
                    <p>
                      <span className="font-medium">Matched user:</span>{" "}
                      {affiliateCandidate.displayName}
                    </p>
                    <p>
                      <span className="font-medium">Username:</span>{" "}
                      <span className="font-mono">{affiliateCandidate.username}</span>
                    </p>
                    <p>
                      <span className="font-medium">Email:</span>{" "}
                      {affiliateCandidate.email}
                    </p>
                    <p>
                      <span className="font-medium">User ID:</span>{" "}
                      <span className="font-mono">{affiliateCandidate.userId}</span>
                    </p>
                  </div>
                )}
                {affiliateError && (
                  <p className="text-sm text-error">{affiliateError}</p>
                )}
                <div className="modal-action mt-0">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={searchAffiliateUser}
                    disabled={affiliateLoading}
                  >
                    {affiliateLoading ? "Searching..." : "Search User"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={addAffiliateToCampaign}
                    disabled={affiliateLoading || !affiliateCandidate}
                  >
                    {affiliateLoading ? "Saving..." : "Confirm & Add Affiliate"}
                  </button>
                  <button type="button" className="btn" onClick={closeAffiliateDialog}>
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-base-200 rounded-lg p-4 text-sm space-y-2">
                  <p>
                    <span className="font-medium">Affiliate user ID:</span>{" "}
                    <span className="font-mono">
                      {affiliateDialogCampaign.affiliateInfo.userID}
                    </span>
                  </p>
                  <p>
                    <span className="font-medium">Current status:</span>{" "}
                    {buildAffiliateDurationRows(
                      affiliateDialogCampaign.affiliateInfo.affiliateDurationsTimesList
                    )[0]?.StartOrEnd === "start"
                      ? "Running"
                      : "Paused"}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {buildAffiliateDurationRows(
                    affiliateDialogCampaign.affiliateInfo
                      .affiliateDurationsTimesList
                  )[0]?.StartOrEnd === "start" ? (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => appendAffiliateStatus("end")}
                      disabled={affiliateLoading}
                    >
                      Pause Program
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => appendAffiliateStatus("start")}
                      disabled={affiliateLoading}
                    >
                      Restart Program
                    </button>
                  )}
                </div>

                {affiliateError && (
                  <p className="text-sm text-error">{affiliateError}</p>
                )}

                <div className="overflow-x-auto max-h-[16rem] overflow-y-auto">
                  <table className="table table-sm table-zebra w-full">
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buildAffiliateDurationRows(
                        affiliateDialogCampaign.affiliateInfo
                          .affiliateDurationsTimesList
                      ).map((row, idx) => (
                        <tr key={`${row.StartOrEnd}-${row.timestamp?.getTime() ?? idx}`}>
                          <td className="uppercase text-xs tracking-wide">
                            {row.StartOrEnd}
                          </td>
                          <td className="text-sm whitespace-nowrap tabular-nums">
                            {formatSignupDate(row.timestamp)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-base-content/50">
                  The latest event determines state: if latest is{" "}
                  <span className="font-mono">start</span>, the affiliate period
                  is currently ongoing.
                </p>

                <div className="modal-action mt-0">
                  <button type="button" className="btn" onClick={closeAffiliateDialog}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
          <div
            className="modal-backdrop bg-black/50"
            role="presentation"
            onClick={closeAffiliateDialog}
          />
        </div>
      )}
    </div>
  );
};

export default CampaignManagement;
